import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  CurrentLocationIcon,
  LocationIcon,
  MapPinnedIcon,
  UserIcon,
} from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import type { SavedLocation } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import type { SearchLocationData } from '@hero/api';
import {
  useAddressApi,
  useAutocompleteApi,
  useSavedLocationListApi,
} from '@hero/api-hooks';
import { useSavedLocationCreateAdapter } from '@hero/web-hooks';
import type { SavedLocationFormValues } from '@hero/web-hooks';
import {
  usePublishOrderStore,
  type PublishOrderLocationDraft,
} from '@hero/hooks-shared';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../../ui/Sheet/Sheet';
import { Button } from '../../../../ui/Button/Button';
import Spinner from '../../../../app/Spinner/Spinner';
import { Input } from '../../../../ui/Input/Input';
import { LocationForm } from '../../../SavedLocation/Form/SavedLocationForm';
import { RegionName } from '../../../../app/RegionName/RegionName';

export interface LocationPickerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Header (e.g. "Pick origin", "Pick destination 2"). */
  title?: string;
  /** Caption under the title. */
  description?: string;
  /** When set we scope the autocomplete + new save to this client. */
  clientId?: string;
  /**
   * The location currently filling the slot the drawer was opened
   * for, if any. Excluded from the "already picked elsewhere" set so
   * the user can re-open the drawer to edit a slot without their own
   * existing pick being treated as a forbidden duplicate.
   */
  excludeLocation?: PublishOrderLocationDraft | null;
  /**
   * When set, the drawer opens directly in **refine mode** with the
   * given location loaded for editing — bypassing the search step
   * entirely. Used by the destination/origin card's "Edit"
   * affordance so the user can tweak the pin or contacts (saved
   * locations) or rename + save a Google pick (unsaved drafts)
   * without re-searching for the same place.
   *
   * Mutually exclusive shapes:
   * - `savedLocationId` — load that record into the form; type +
   *   client are locked to whatever the record holds (see
   *   `lockOnLoad` on `useSavedLocationForm`). On Save the API runs
   *   `update`, not `create`.
   * - `draft` — pre-fill name + coords from a publish-order draft
   *   (no `savedLocationId`). The form starts unlocked so the user
   *   can save it as a new SavedLocation, or skip-save and just keep
   *   using the edited draft.
   */
  initialEdit?: { savedLocationId?: string; draft?: PublishOrderLocationDraft };
  onPick: (location: PublishOrderLocationDraft) => void;
}

type DrawerMode = 'search' | 'refine';

/**
 * Location picker drawer — single-surface search + refine flow.
 *
 * 1. **Search mode** — two groups, all visible without typing so the
 *    drawer never shows a blank state when the user opens it:
 *      - **Your saved locations** — pre-fetched via
 *        `useSavedLocationListApi` (top 10, scoped to the picked
 *        client when one is set; otherwise the user's own places).
 *        Saved-location rows that the publish session has already
 *        consumed (origin or another destination) get an
 *        "Already picked" badge so the carrier doesn't accidentally
 *        repeat the same warehouse across slots — the badge is a hint
 *        only, the row stays clickable for the rare repeat case.
 *      - **Places** (Google results — picking advances to refine).
 *    Once the user types, the saved-locations list switches from the
 *    pre-fetched defaults to the autocomplete server hits (which are
 *    relevance-scored against the query).
 *
 * 2. **Refine mode** — the shared {@link LocationForm} is embedded
 *    *inside* the drawer (no nested modal). The form already carries
 *    the full SavedLocation experience: draggable pin via
 *    `SavedLocationMap`, in-map autocomplete, name + contact + URL
 *    fields, and (when a `clientId` is picked) a locked
 *    {@link ClientLinkingBanner}. Two terminal actions:
 *      - **Save & use** — validate + persist via
 *        `useSavedLocationCreateAdapter`, push the `SavedLocation`
 *        into the publish draft, close the drawer.
 *      - **Use without saving** — skip persistence and apply the
 *        Google pick verbatim (lat/lng + name + address). Marked
 *        `isAccurate: false` so the side-panel surfaces the
 *        approximate-stops warning — the user can still publish, but
 *        dispatch knows to chase a precise drop.
 *
 * The drawer is intentionally stateless w.r.t. *which* slot it fills —
 * the host (RouteSection origin / destination row) wires `onPick` to
 * the correct store setter. It does, however, read the full origin +
 * destinations from the publish-order store so the "Already picked"
 * badge can render without the host threading that state down.
 */
export function LocationPickerDrawer({
  isOpen,
  onClose,
  title,
  description,
  clientId,
  excludeLocation,
  initialEdit,
  onPick,
}: LocationPickerDrawerProps) {
  const { t } = useTranslation(['m-order', 'm-location', 'c-main']);

  const [mode, setMode] = useState<DrawerMode>('search');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [pickedPlace, setPickedPlace] = useState<SearchLocationData | null>(null);
  const [isLocatingMe, setIsLocatingMe] = useState(false);

  // Reverse-geocode coordinates → full address. Used by the
  // "Use my current location" affordance below the search input so
  // the refine view can show a real address card instead of raw
  // lat/lng.
  const addressApi = useAddressApi();

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setDebounced('');
    setIsLocatingMe(false);
    if (initialEdit?.savedLocationId || initialEdit?.draft) {
      // Edit mode — drawer opens directly into the refine view with
      // the loaded record / draft. No search step.
      setMode('refine');
      setPickedPlace(null);
    } else {
      setMode('search');
      setPickedPlace(null);
    }
  }, [isOpen, initialEdit]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const autocomplete = useAutocompleteApi(
    { search: debounced },
    { enabled: isOpen && debounced.length > 0 },
  );

  // Pre-fetch the user's saved locations so the drawer renders the
  // first ~10 places the moment it opens — no blank state until the
  // carrier types something. Scoped to the picked client when one is
  // set (mirrors the autocomplete grouping behaviour); otherwise
  // returns the user's own places.
  const defaultSaved = useSavedLocationListApi(
    clientId ? { clientId: getObjectId(clientId) } : {},
    { enabled: isOpen, limit: 10 },
  );

  const sessionExtras = usePublishOrderStore(s => s.savedLocationsExtras);
  const addSessionExtra = usePublishOrderStore(s => s.addSavedLocationExtra);
  // Read what the publish-order session has already picked so we can
  // hard-block duplicates: the same saved-location id, or the same
  // lat/lng (rounded to ~5 decimals ≈ 1m), can only sit in one slot
  // at a time. The slot the drawer was opened to edit is removed
  // from these sets so the user's own existing pick isn't treated as
  // a forbidden duplicate when they reopen the drawer to refine.
  const origin = usePublishOrderStore(s => s.origin);
  const destinations = usePublishOrderStore(s => s.destinations);
  const isSameLocation = (
    a: PublishOrderLocationDraft | null | undefined,
    b: PublishOrderLocationDraft | null | undefined,
  ) => {
    if (!a || !b) return false;
    if (a.savedLocationId && b.savedLocationId) {
      return a.savedLocationId === b.savedLocationId;
    }
    return roundCoord(a.lat) === roundCoord(b.lat) && roundCoord(a.lng) === roundCoord(b.lng);
  };
  const { pickedSavedIds, pickedCoordKeys } = useMemo(() => {
    const ids = new Set<string>();
    const coords = new Set<string>();
    const collect = (loc: PublishOrderLocationDraft | null | undefined) => {
      if (!loc || loc.lat === 0) return;
      if (excludeLocation && isSameLocation(loc, excludeLocation)) return;
      if (loc.savedLocationId) ids.add(loc.savedLocationId);
      coords.add(coordKey(loc.lat, loc.lng));
    };
    collect(origin);
    for (const d of destinations) collect(d);
    return { pickedSavedIds: ids, pickedCoordKeys: coords };
    // `isSameLocation` is a stable closure within the render — its
    // identity changes per render but the inputs are captured in the
    // deps array so the memo updates correctly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, destinations, excludeLocation]);

  const savedResults = useMemo<SavedLocation[]>(() => {
    // When the user is searching, prefer the autocomplete server hits
    // (they're scored on relevance). When idle, fall back to the
    // pre-fetched default list so the drawer always has *something*
    // to show. In both modes we fold in `sessionExtras` (places the
    // user just created in this session, before React Query
    // invalidation lands) and dedupe by id.
    const fromApi =
      debounced.length > 0
        ? autocomplete.data?.savedLocations ?? []
        : defaultSaved.data ?? [];
    const ids = new Set(fromApi.map(s => String(s._id)));
    const extras = sessionExtras.filter(s => !ids.has(String(s._id)));
    return [...fromApi, ...extras];
  }, [autocomplete.data, defaultSaved.data, sessionExtras, debounced]);

  const googleResults = autocomplete.data?.googlePlaces ?? [];

  const handlePickSaved = (saved: SavedLocation) => {
    if (pickedSavedIds.has(String(saved._id))) return;
    const draft = savedLocationToDraft(saved);
    if (pickedCoordKeys.has(coordKey(draft.lat, draft.lng))) return;
    onPick(draft);
    onClose();
  };

  const handlePickGoogle = (place: SearchLocationData) => {
    if (pickedCoordKeys.has(coordKey(place.location.lat, place.location.lng))) return;
    setPickedPlace(place);
    setMode('refine');
  };

  /**
   * Fetch the user's browser geolocation, reverse-geocode it via
   * `useAddressApi`, then route the result through the refine flow
   * exactly like a Google pick. Same fallback ladder as
   * `useMapCurrentLocation`: high-accuracy first → low-accuracy
   * (WiFi/IP-based) → give up.
   */
  const handleUseCurrentLocation = () => {
    if (isLocatingMe) return;
    if (!navigator.geolocation) return;
    setIsLocatingMe(true);
    const handlePosition = async (position: GeolocationPosition) => {
      try {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const address = await addressApi
          .mutateAsync({ lat, lng })
          .catch(() => undefined);
        const synthetic: SearchLocationData = {
          name:
            address?.fullAddress?.split(',')[0]?.trim() ||
            t('wizard.location_picker.current_location.label', {
              defaultValue: 'My current location',
            }),
          fullAddress: address?.fullAddress ?? `${lat}, ${lng}`,
          location: { lat, lng },
          placeId: '',
        };
        if (pickedCoordKeys.has(coordKey(lat, lng))) {
          setIsLocatingMe(false);
          return;
        }
        setPickedPlace(synthetic);
        setMode('refine');
      } finally {
        setIsLocatingMe(false);
      }
    };
    navigator.geolocation.getCurrentPosition(
      handlePosition,
      () => {
        navigator.geolocation.getCurrentPosition(
          handlePosition,
          () => setIsLocatingMe(false),
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  const handleLocationSaved = (saved: SavedLocation) => {
    addSessionExtra(saved);
    onPick(savedLocationToDraft(saved));
    onClose();
  };

  /**
   * Reverse-geocode `(lat, lng)` to refresh `city` + `fullAddress`
   * on the draft. Called from `handleSkipSave` so an unsaved draft
   * that gets its pin dragged from one city to another doesn't keep
   * stale name/address/city on the destination card. If the API call
   * fails we return the draft unchanged rather than block the pick.
   */
  const refreshDraftAddress = async (
    draft: PublishOrderLocationDraft,
  ): Promise<PublishOrderLocationDraft> => {
    if (draft.lat === 0 && draft.lng === 0) return draft;
    const address = await addressApi
      .mutateAsync({ lat: draft.lat, lng: draft.lng })
      .catch(() => undefined);
    if (!address) return draft;
    return {
      ...draft,
      fullAddress: address.fullAddress ?? draft.fullAddress,
      city: address.city?.name ?? draft.city,
    };
  };

  const handleSkipSave = async (values: SavedLocationFormValues) => {
    // Edit-mode of an unsaved draft: persist the form tweaks back
    // onto the draft (pin nudge, rename, accuracy flip) without
    // creating a SavedLocation. Contacts can't carry across because
    // they're CRM contact ids — only meaningful when bound to a
    // saved record.
    if (initialEdit?.draft) {
      const prev = initialEdit.draft;
      const nextLat = values.location?.lat ?? prev.lat;
      const nextLng = values.location?.lng ?? prev.lng;
      const coordsChanged = nextLat !== prev.lat || nextLng !== prev.lng;
      // If the pin moved, drop the stale name/city/address — the
      // reverse-geocode below fills them back in for the new pin.
      // If the user explicitly renamed, keep the new name.
      const userRenamed = !!values.name?.trim() && values.name.trim() !== prev.name;
      let next: PublishOrderLocationDraft = {
        ...prev,
        name: values.name?.trim() || (coordsChanged ? '' : prev.name),
        fullAddress: coordsChanged ? undefined : prev.fullAddress,
        city: coordsChanged ? undefined : prev.city,
        lat: nextLat,
        lng: nextLng,
        isAccurate: values.isAccurate ?? false,
        // A link the user pasted in the form wins; otherwise the old
        // one survives only while the pin hasn't moved, since it stops
        // describing this stop the moment it does.
        locationUrl: values.locationUrl?.trim() || (coordsChanged ? undefined : prev.locationUrl),
      };
      if (coordsChanged) {
        next = await refreshDraftAddress(next);
        // Name fallback after re-geocode: user's explicit rename wins;
        // otherwise use the new city, then the fresh fullAddress head,
        // then whatever was there before.
        if (!userRenamed) {
          next.name = next.city || next.fullAddress?.split(',')[0]?.trim() || prev.name;
        }
      }
      onPick(next);
      onClose();
      return;
    }
    if (!pickedPlace) return;
    // Fresh Google pick → no save: respect the form snapshot
    // (accuracy toggle, refined pin, optional rename) instead of
    // hard-resetting to the un-edited Google place + isAccurate=false.
    // The accuracy switch can't sync if we ignore `values.isAccurate`.
    const base = googleResultToDraft(pickedPlace, values.isAccurate ?? false);
    const nextLat = values.location?.lat ?? base.lat;
    const nextLng = values.location?.lng ?? base.lng;
    const coordsChanged = nextLat !== base.lat || nextLng !== base.lng;
    let next: PublishOrderLocationDraft = {
      ...base,
      name: values.name?.trim() || base.name,
      lat: nextLat,
      lng: nextLng,
      // `googleResultToDraft` doesn't set `city`, so always
      // reverse-geocode a fresh pick to attach it. The card uses
      // `city` as the primary label for unsaved locations so a
      // stale first-pick name never masks the truth.
      fullAddress: coordsChanged ? undefined : base.fullAddress,
      locationUrl: values.locationUrl?.trim() || undefined,
    };
    next = await refreshDraftAddress(next);
    onPick(next);
    onClose();
  };

  const handleBack = () => {
    // In edit mode (no search step) Back closes the drawer; in pick
    // mode Back rolls the user back to the search list.
    if (mode === 'refine' && (initialEdit?.savedLocationId || initialEdit?.draft)) {
      onClose();
    } else if (mode === 'refine') {
      setMode('search');
    } else {
      onClose();
    }
  };

  const isEditingSaved = !!initialEdit?.savedLocationId;
  const isEditingDraft = !!initialEdit?.draft;
  const isEditing = isEditingSaved || isEditingDraft;

  const headerTitle =
    mode === 'refine'
      ? isEditing
        ? t('wizard.location_picker.edit_title', {
            defaultValue: 'Edit this location',
          })
        : t('wizard.location_picker.refine_title', {
            defaultValue: 'Refine and save this location',
          })
      : title ?? t('wizard.location_picker.title', { defaultValue: 'Pick a location' });

  const headerDescription =
    mode === 'refine'
      ? isEditingSaved
        ? t('wizard.location_picker.edit_saved_description', {
            defaultValue:
              'Update the contacts or move the pin. The place type and client stay locked to this saved record.',
          })
        : isEditingDraft
        ? t('wizard.location_picker.edit_draft_description', {
            defaultValue:
              "Tweak the pin, name, or contacts. Save it for next time, or keep using it just for this order.",
          })
        : t('wizard.location_picker.refine_description', {
            defaultValue:
              'Drop the exact pin on the map, name it, and we\'ll save it for next time. Or use it once without saving.',
          })
      : description;

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex flex-col p-0 bg-white w-[880px] sm:max-w-[880px]">
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-start gap-2">
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center justify-center rounded-full bg-gray-100 size-8 hover:bg-gray-200 flex-shrink-0">
              <ArrowLeftIcon className="size-4 rtl:rotate-180" />
            </button>
            <div className="min-w-0 flex-1">
              <SheetTitle>{headerTitle}</SheetTitle>
              {headerDescription && <SheetDescription>{headerDescription}</SheetDescription>}
            </div>
          </div>
        </SheetHeader>

        {mode === 'search' && (
          <SearchView
            query={query}
            onQueryChange={setQuery}
            googleResults={googleResults}
            savedResults={savedResults}
            pickedSavedIds={pickedSavedIds}
            pickedCoordKeys={pickedCoordKeys}
            isLoading={autocomplete.isLoading || (debounced.length === 0 && defaultSaved.isLoading)}
            isLocatingMe={isLocatingMe}
            onPickGoogle={handlePickGoogle}
            onPickSaved={handlePickSaved}
            onUseCurrentLocation={handleUseCurrentLocation}
          />
        )}

        {mode === 'refine' && (pickedPlace || isEditingSaved || isEditingDraft) && (
          <RefineView
            place={pickedPlace}
            editSavedLocationId={initialEdit?.savedLocationId}
            editDraft={initialEdit?.draft}
            clientId={clientId}
            onSaved={handleLocationSaved}
            onSkip={handleSkipSave}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

interface SearchViewProps {
  query: string;
  onQueryChange: (next: string) => void;
  googleResults: SearchLocationData[];
  savedResults: SavedLocation[];
  /** Saved-location ids already picked elsewhere in this publish session. */
  pickedSavedIds: Set<string>;
  /** Rounded "lat,lng" keys already picked elsewhere — blocks Google duplicates. */
  pickedCoordKeys: Set<string>;
  isLoading: boolean;
  /** Spinner state for the "Use my current location" button. */
  isLocatingMe: boolean;
  onPickGoogle: (place: SearchLocationData) => void;
  onPickSaved: (saved: SavedLocation) => void;
  onUseCurrentLocation: () => void;
}

function SearchView({
  query,
  onQueryChange,
  googleResults,
  savedResults,
  pickedSavedIds,
  pickedCoordKeys,
  isLoading,
  isLocatingMe,
  onPickGoogle,
  onPickSaved,
  onUseCurrentLocation,
}: SearchViewProps) {
  const { t } = useTranslation(['m-order', 'c-main']);

  const hasQuery = query.trim().length > 0;
  const isEmpty =
    !isLoading &&
    savedResults.length === 0 &&
    googleResults.length === 0;

  return (
    <>
      <div className="border-b px-6 py-3">
        <div className="flex items-center gap-2">
          <Input
            autoFocus
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            placeholder={t('wizard.location_picker.search_placeholder', {
              defaultValue: 'Search a city, neighborhood, port or place…',
            })}
            className="flex-1"
          />
          {/*
           * Compact "use my current location" affordance, tucked
           * inline at the end of the search row so it's reachable
           * but doesn't eat its own line. Mirrors a Google pick:
           * we reverse-geocode the coords, build a synthetic
           * SearchLocationData, and route through the refine view.
           * The label stays a short "Current location" — the
           * icon carries most of the meaning.
           */}
          <button
            type="button"
            onClick={onUseCurrentLocation}
            disabled={isLocatingMe}
            title={t('wizard.location_picker.current_location.cta', {
              defaultValue: 'Use my current location',
            })}
            className={cn(
              'flex h-10 items-center gap-1.5 rounded-md border border-gray-200 px-3 text-xs text-gray-700 whitespace-nowrap transition-colors flex-shrink-0',
              isLocatingMe
                ? 'opacity-60 cursor-wait'
                : 'hover:bg-gray-50 hover:border-gray-300',
            )}>
            {isLocatingMe ? (
              <Spinner className="size-3.5 text-primary" />
            ) : (
              <CurrentLocationIcon className="size-3.5 text-primary" />
            )}
            <span className="font-medium">
              {isLocatingMe
                ? t('wizard.location_picker.current_location.locating_short', {
                    defaultValue: 'Locating\u2026',
                  })
                : t('wizard.location_picker.current_location.short', {
                    defaultValue: 'Current location',
                  })}
            </span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="px-6 py-8 text-center text-sm text-gray-400">
            {hasQuery
              ? t('wizard.location_picker.searching', { defaultValue: 'Searching…' })
              : t('c-main:loading', { defaultValue: 'Loading…' })}
          </div>
        ) : isEmpty ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            {hasQuery
              ? t('wizard.location_picker.no_results', {
                  defaultValue: 'No places match your search.',
                })
              : t('wizard.location_picker.search_hint', {
                  defaultValue:
                    'Type to search your saved locations, cities, and the map.',
                })}
          </div>
        ) : (
          <>
            {savedResults.length > 0 && (
              <div className="border-b">
                <div className="px-6 py-2 text-[11px] uppercase tracking-wide text-gray-400 bg-gray-50">
                  {hasQuery
                    ? t('wizard.location_picker.saved_group', {
                        defaultValue: 'Saved locations',
                      })
                    : t('wizard.location_picker.recent_saved_group', {
                        defaultValue: 'Your saved locations',
                      })}
                </div>
                <ul className="divide-y">
                  {savedResults.map(s => {
                    const id = String(s._id);
                    const [lng, lat] = s.address.location.coordinates;
                    const isAlreadyPicked =
                      pickedSavedIds.has(id) || pickedCoordKeys.has(coordKey(lat, lng));
                    return (
                      <li key={id}>
                        <button
                          type="button"
                          onClick={() => onPickSaved(s)}
                          disabled={isAlreadyPicked}
                          className={cn(
                            'flex w-full items-center gap-3 px-6 py-3 text-start transition-colors',
                            isAlreadyPicked
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-gray-50',
                          )}>
                          <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary flex-shrink-0">
                            <MapPinnedIcon className="size-4" />
                          </div>
                          {/*
                           * Two-row layout, both rows stretched
                           * between left and right edges so each
                           * pair tells its own story:
                           *   Row 1: name (left) ........ city (right)
                           *   Row 2: address (left) ..... owner (right)
                           * The "Already picked" badge tucks into
                           * the top-right alongside the city chip
                           * when applicable so duplicates surface
                           * without breaking the grid rhythm.
                           */}
                          <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-gray-900 truncate min-w-0">
                                {s.name}
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {isAlreadyPicked && (
                                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-800">
                                    {t('wizard.location_picker.already_picked_badge', {
                                      defaultValue: 'Already picked',
                                    })}
                                  </span>
                                )}
                                {s.address.city?.name && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                                    <LocationIcon className="size-3" />
                                    <RegionName
                                      customCode={s.address.city.customCode}
                                      fallback={s.address.city.name}
                                    />
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                              <div
                                className="truncate min-w-0"
                                title={s.address.fullAddress}>
                                {s.address.fullAddress}
                              </div>
                              <span className="inline-flex items-center gap-1 flex-shrink-0 text-gray-600">
                                <UserIcon className="size-3 text-gray-400" />
                                <span className="font-medium">
                                  {s.client?.name ??
                                    t('wizard.location.my_place', {
                                      defaultValue: 'My place',
                                    })}
                                </span>
                              </span>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {googleResults.length > 0 && (
              <div>
                <div className="px-6 py-2 text-[11px] uppercase tracking-wide text-gray-400 bg-gray-50">
                  {t('wizard.location_picker.places_group', {
                    defaultValue: 'Places',
                  })}
                </div>
                <ul className="divide-y">
                  {googleResults.map(place => {
                    const isAlreadyPicked = pickedCoordKeys.has(
                      coordKey(place.location.lat, place.location.lng),
                    );
                    return (
                      <li key={place.placeId}>
                        <button
                          type="button"
                          onClick={() => onPickGoogle(place)}
                          disabled={isAlreadyPicked}
                          className={cn(
                            'flex w-full items-start gap-3 px-6 py-3 text-start transition-colors',
                            isAlreadyPicked
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-gray-50',
                          )}>
                          <div className="mt-0.5 flex size-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 flex-shrink-0">
                            <LocationIcon className="size-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {place.name}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {place.fullAddress}
                            </div>
                          </div>
                          {isAlreadyPicked && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-800 flex-shrink-0">
                              {t('wizard.location_picker.already_picked_badge', {
                                defaultValue: 'Already picked',
                              })}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

interface RefineViewProps {
  /**
   * Fresh Google pick from search → refine. Mutually exclusive with
   * `editSavedLocationId` and `editDraft`; when none are set the
   * RefineView is not rendered.
   */
  place?: SearchLocationData | null;
  /**
   * Edit-mode: the saved-location id to load into the form.
   * `useSavedLocationCreateAdapter` handles the load + auto-locks
   * the type/client to the record's values via `lockOnLoad`. On
   * Save the API runs `update`, not `create`.
   */
  editSavedLocationId?: string;
  /**
   * Edit-mode: an unsaved publish-order draft to prefill (a Google
   * pick the user already accepted earlier). Behaves like a fresh
   * Google pick but seeded from the existing draft so the user can
   * tweak it without re-searching.
   */
  editDraft?: PublishOrderLocationDraft;
  clientId?: string;
  onSaved: (saved: SavedLocation) => void;
  /**
   * Skip-save / "use without saving". The current form snapshot is
   * passed so the drawer can persist the user's pin/name tweaks
   * back to the order draft even when no SavedLocation is created.
   * Hidden in edit-saved mode (the record already exists; the only
   * meaningful action is "Save changes").
   */
  onSkip: (values: SavedLocationFormValues) => void;
}

/**
 * Embedded SavedLocation form. Three call shapes converge here:
 *
 *  1. **Search → Google pick**: `place` is set. Form starts empty
 *     except for name + coords from the place; the user fills in
 *     the rest and either saves (becomes a SavedLocation) or skips
 *     (uses the pin as-is in the order).
 *
 *  2. **Edit a saved location**: `editSavedLocationId` is set. The
 *     form loads the record, locks type + client, and Save runs the
 *     `update` API. Skip-save is hidden — the record already
 *     exists, the only meaningful action is "Save changes".
 *
 *  3. **Edit an unsaved draft**: `editDraft` is set. Same as
 *     case 1 but seeded from the draft so the user keeps their
 *     prior pin / name. Skip-save returns the draft (possibly
 *     edited) to the order without persisting.
 */
function RefineView({
  place,
  editSavedLocationId,
  editDraft,
  clientId,
  onSaved,
  onSkip,
}: RefineViewProps) {
  const { t } = useTranslation(['m-order', 'm-location', 'c-main']);

  const isEditingSaved = !!editSavedLocationId;
  // For saved-record edits the loaded record is the source of truth
  // (and the type/client lock is driven by `lockOnLoad`); we don't
  // pass any caller-side `defaultValues` so we don't fight the
  // hook's reset-after-load. For Google picks and unsaved-draft
  // edits we seed name + coords from whichever input shape is set.
  const seedName = !isEditingSaved ? place?.name ?? editDraft?.name : undefined;
  // `seedLocation` has to be ref-stable across renders: it flows into
  // `useSavedLocationForm`'s `defaultValues` and any new identity
  // would invalidate downstream memos / effects (ultimately looping
  // the map's `setLocations` effect into a max-update-depth crash).
  const seedLocation = useMemo(() => {
    if (isEditingSaved) return undefined;
    if (place) return place.location;
    if (editDraft) return { lat: editDraft.lat, lng: editDraft.lng };
    return undefined;
  }, [isEditingSaved, place, editDraft]);

  // The CRM client lock only applies to create/draft flows; saved
  // records carry their own client (or no client at all) and their
  // lock is owned by the form hook's `lockOnLoad` path. Memoized
  // because `getObjectId` mints a fresh `ObjectId` instance on every
  // call — without the memo, every parent render would feed
  // `useSavedLocationForm` a brand-new `forceClientId` reference,
  // which would re-derive `setSavedLocation` (its useCallback dep)
  // and re-fire the adapter's `setSavedLocation` effect every render
  // → repeated `form.reset` → infinite map re-entry.
  const forceClientId = useMemo(
    () => (clientId && !isEditingSaved ? getObjectId(clientId) : undefined),
    [clientId, isEditingSaved],
  );
  // Same story as `forceClientId`: re-keying the saved-location query
  // and the adapter's reset effect every render is exactly what
  // crashed the map. Memoize so the `ObjectId` ref is stable for a
  // given `editSavedLocationId` string.
  const stableSavedLocationId = useMemo(
    () => (editSavedLocationId ? getObjectId(editSavedLocationId) : undefined),
    [editSavedLocationId],
  );
  // A stop that already carries a Maps link pre-fills the form's link
  // field, so saving it as a place keeps the same provenance instead
  // of asking the user to paste what they just pasted.
  const seedLocationUrl = !isEditingSaved ? editDraft?.locationUrl : undefined;
  const adapterDefaultValues = useMemo(
    () =>
      !isEditingSaved && (seedName || seedLocation || seedLocationUrl)
        ? {
            name: seedName,
            location: seedLocation,
            locationUrl: seedLocationUrl,
            clientId: forceClientId,
          }
        : undefined,
    [isEditingSaved, seedName, seedLocation, seedLocationUrl, forceClientId],
  );

  const forceType = clientId && !isEditingSaved ? ('client' as const) : undefined;
  const {
    createLocation,
    updateLocation,
    formHook,
    onSaveClick,
    savedLocation: savedLocationQuery,
  } = useSavedLocationCreateAdapter({
    onLocationSaved: onSaved,
    savedLocationId: stableSavedLocationId,
    forceType,
    forceClientId,
    defaultValues: adapterDefaultValues,
  });
  // For saved-record edits the loaded address is the source of truth
  // for what "this place" looks like. Passing it down lets
  // `SavedLocationMap` skip its auto-enter-edit path (which is meant
  // for *new* picks where the user must confirm the pin) — otherwise
  // the map mounts in edit mode, flips
  // `formHook.isLocationEditing` true, and Save stays disabled even
  // after the user changes name / contact / URL. Fresh Google picks
  // and unsaved-draft edits keep the auto-enter behaviour because
  // their pin still needs an explicit confirmation.
  const initialAddress = savedLocationQuery.data?.address;

  const isSaving = createLocation.isPending || updateLocation.isPending;
  const disableSave = !formHook.isFormValid() || formHook.isLocationEditing || isSaving;
  // Skip-save only makes sense in create flows (Google pick or
  // edit-of-unsaved-draft). When editing a real saved record there
  // is no "use without saving" — the record exists and the only
  // meaningful action is to persist changes.
  const showSkipSave = !isEditingSaved;

  return (
    <>
      <div className="flex-1 overflow-y-auto p-6">
        <LocationForm
          formHook={formHook}
          isLoading={isSaving}
          hideSubmitButton
          initialAddress={initialAddress}
        />
      </div>

      <div className="border-t p-4 flex items-center justify-end gap-2">
        {showSkipSave && (
          <Button
            variant="outline"
            onClick={() => onSkip(formHook.form.getValues())}
            disabled={isSaving}>
            {t('wizard.location_picker.skip_save', {
              defaultValue: 'Use without saving',
            })}
          </Button>
        )}
        <Button onClick={onSaveClick} disabled={disableSave}>
          {isSaving ? (
            <>
              <Spinner className="me-2" />
              {t('c-main:async_operations.saving', { defaultValue: 'Saving\u2026' })}
            </>
          ) : isEditingSaved ? (
            t('wizard.location_picker.save_changes', {
              defaultValue: 'Save changes',
            })
          ) : (
            t('wizard.location_picker.save_and_use', {
              defaultValue: 'Save & use',
            })
          )}
        </Button>
      </div>
    </>
  );
}

function savedLocationToDraft(
  saved: SavedLocation,
  override?: { isAccurate?: boolean },
): PublishOrderLocationDraft {
  const [lng, lat] = saved.address.location.coordinates;
  // Lift the primary contact's phone + name onto the draft so the
  // payload builder can use them as a receiver-phone fallback (the
  // explicit "Receiver phone" input was removed from the publish
  // page) and the destination/origin row can show who the driver
  // should call on arrival without re-opening the picker.
  const primary = saved.contacts?.find(c => c.isPrimary) ?? saved.contacts?.[0];
  // Honor whatever the saved record says about accuracy. Pre-`isAccurate`
  // documents fall through to `true` via the schema default. The
  // optional override exists for callers (today: none) that need to
  // force a flag when materializing a saved row from an external
  // context (e.g. legacy importers).
  const baseIsAccurate = saved.isAccurate ?? true;
  return {
    savedLocationId: String(saved._id),
    name: saved.name,
    fullAddress: saved.address.fullAddress,
    city: saved.address.city?.name,
    lat,
    lng,
    isAccurate: override?.isAccurate ?? baseIsAccurate,
    // The place's own Maps link travels onto the order — a stop that
    // came from the CRM should point at the same pin the CRM does.
    locationUrl: saved.locationUrl,
    contactPhone: primary?.phone,
    contactName: primary?.name,
  };
}

function googleResultToDraft(
  place: SearchLocationData,
  isAccurate: boolean,
): PublishOrderLocationDraft {
  return {
    name: place.name,
    fullAddress: place.fullAddress,
    lat: place.location.lat,
    lng: place.location.lng,
    placeId: place.placeId,
    isAccurate,
  };
}

/**
 * Round to ~5 decimals so two pins within ~1m collapse to the same
 * key. Used to dedupe across slots when the user picks "the same
 * spot" twice via different Google results / saved locations.
 */
function roundCoord(value: number): number {
  return Math.round(value * 1e5) / 1e5;
}

function coordKey(lat: number, lng: number): string {
  return `${roundCoord(lat)},${roundCoord(lng)}`;
}
